import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { CONSTANT } from "../../../constant/Constant";
import { CHATBOT } from "../../../api/Chatbot";
import { CONTACT } from "../../../api/Contact";

export default function ChatBot({ isOpen, setIsOpen }) {
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: "Hello! Welcome to ENCORE Events. Ask me anything.",
    },
  ]);
  const [companyInfo, setCompanyInfo] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const bookingTriggers = companyInfo.filter(
    (item) => item.category === "booking"
  );
  const checkBookingTriggers = (text) => {
    return bookingTriggers[0].phrases.some((trigger) =>
      text.toLowerCase().includes(trigger)
    );
  };
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookingStage, setBookingStage] = useState(null);
  const [bookingData, setBookingData] = useState({});

  const getReplyFromTriggers = (message, triggers) => {
    const userMessage = message.toLowerCase();
    for (const trigger of triggers) {
      if (
        trigger.phrases.some((phrase) =>
          userMessage.includes(phrase.toLowerCase())
        )
      ) {
        return trigger.reply;
      }
    }
    return null; // Return null instead of a fallback here
  };

  const handleEnquiry = async (values) => {
    setLoading(true);
    var request = {
      name: values.name,
      place: values.place,
      mobile: values.phone,
      email: values.email,
      message: `I would like to enqure for a ${values.eventType} at the date ${values.date}.  -------(enquired through chatbot)`,
      isViewed: false,
      isPurged: false,
    };
    try {
      const res = await CONTACT.ADD(request);
      if (res.status === 200) {
      } else {
        throw new Error("failed to create an topic");
      }
    } catch (error) {
      // console.log("error", error);
    }
  };

  const messagesEndRef = useRef(null);
  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMsg = { sender: "user", text: input };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    // 1. Handle custom keyword (company name)
    if (companyInfo) {
      let replies = [];
      if (checkBookingTriggers(input)) {
        setBookingStage("name");
        setBookingData({});
        setMessages((prev) => [
          ...prev,
          {
            sender: "bot",
            text: "Great! Let's book your event. What's your name?",
          },
        ]);
        setLoading(false);
        return;
      }
      const matchedReply = getReplyFromTriggers(input, companyInfo);
      if (matchedReply) {
        setMessages((prev) => [...prev, { sender: "bot", text: matchedReply }]);
        setLoading(false);
        return;
      }

      // Booking conversation flow
      if (bookingStage) {
        const nextStageMap = {
          name: "eventType",
          eventType: "date",
          date: "phone",
          phone: "place",
          place: "email",
          email: "done",
        };

        const questions = {
          name: "What type of event are you planning?",
          eventType: "On which date is your event?",
          date: "Please share your phone number for contacting.",
          phone: "Please share your email for confirmation.",
          place: "Please share your location.",
          email:
            "Thanks! We've received your request. Wn e'll get back to you soon.",
        };

        const updatedData = { ...bookingData, [bookingStage]: input };
        setBookingData(updatedData);

        const nextStage = nextStageMap[bookingStage];

        if (nextStage === "done") {
          setMessages((prev) => [
            ...prev,
            { sender: "bot", text: questions[bookingStage] },
          ]);
          // send to admin here
          // axios.post(`${CONSTANT.ADMIN_NOTIFICATION_API}`, {
          //   type: "new_booking",
          //   data: updatedData,
          // });
          await handleEnquiry(updatedData);
          setBookingStage(null);
          setBookingData({});
        } else {
          setMessages((prev) => [
            ...prev,
            { sender: "bot", text: questions[bookingStage] },
          ]);
          setBookingStage(nextStage);
        }
        setLoading(false);
        return;
      }

      if (replies.length > 0) {
        const combinedReply = replies.join("\n\n"); // 👈 This separates answers into paragraphs
        setMessages((prev) => [
          ...prev,
          { sender: "bot", text: combinedReply },
        ]);
        setLoading(false);
        return;
      }
    }

    // 2. Else call the API
    try {
      const response = await axios.post(
        "https://openrouter.ai/api/v1/chat/completions",
        {
          model: "mistralai/mistral-7b-instruct",
          messages: [
            { role: "system", content: "You are a helpful assistant." },
            ...messages
              .filter((msg) => msg.sender !== "system")
              .map((msg) => ({
                role: msg.sender === "user" ? "user" : "assistant",
                content: msg.text,
              })),
            { role: "user", content: input },
          ],
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${CONSTANT.OPEN_AI_APIKEY}`,
            "HTTP-Referer": "http://localhost:3000",
            "X-Title": "React ChatBot",
          },
        }
      );

      const botReply = response.data.choices[0].message.content;
      setMessages((prev) => [...prev, { sender: "bot", text: botReply }]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: "Oops, something went wrong." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") sendMessage();
  };
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);
  const getMemoryData = async () => {
    try {
      const res = await CHATBOT.GET_ALL();
      const botData = res.data[0];
      if (res.status !== 200) {
        throw new Error("Failed to fetch brand information");
      }
      if (botData !== undefined) setCompanyInfo(botData.trigger);
    } catch (error) {
      // console.log("Error loading topics: ", error);
    }
  };
  useEffect(() => {
    getMemoryData();
  }, []);
  return (
    <div className="fixed bottom-4 right-4 z-50">
      {isOpen && (
        <div
          className={`${
            isFullscreen
              ? "fixed inset-0 m-0 w-full h-full"
              : "mt-2 w-80 sm:w-96 h-[28rem]"
          } bg-white rounded-lg shadow-xl flex flex-col transition-all duration-300`}
        >
          {/* <div className="mt-2 w-80 sm:w-96 h-[28rem] bg-white rounded-lg shadow-xl flex flex-col"> */}
          <div className="bg-primary text-white p-3 rounded-t-lg font-semibold flex justify-between items-center">
            <span>🤖 ChatBot</span>
            <div>
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="text-white mr-2"
                title={isFullscreen ? "Minimize" : "Fullscreen"}
              >
                {isFullscreen ? "▼" : "▲"}
              </button>
              <button onClick={() => setIsOpen(false)} className="text-white">
                ✕
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${
                  msg.sender === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`px-3 py-2 rounded-lg text-sm max-w-[75%] ${
                    msg.sender === "user"
                      ? "bg-primary text-white rounded-br-none"
                      : "bg-gray text-black rounded-bl-none"
                  }`}
                >
                  {/^[-•\d]/.test(msg.text.trim()) ||
                  msg.text.includes("\n-") ||
                  msg.text.includes("\n•") ? (
                    <ul className="list-disc pl-5 space-y-1">
                      {msg.text
                        .split("\n")
                        .filter((line) => line.trim() !== "")
                        .map((line, i) => (
                          <li key={i}>
                            {linkifyText(line.replace(/^[-•\d.]+\s*/, ""))}
                          </li>
                        ))}
                    </ul>
                  ) : (
                    <p className=" break-words whitespace-pre-wrap max-w-full inline-block">
                      {linkifyText(msg.text)}
                    </p>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="text-sm text-gray-500">🤖 Bot is typing...</div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <div className="p-2 border-t flex w-full flex-nowrap">
            <input
              type="text"
              className="flex-grow min-w-0 border rounded-l-lg p-2 text-sm focus:outline-none"
              placeholder="Type a message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button
              className="bg-primary text-white px-4 rounded-r-lg whitespace-nowrap"
              onClick={sendMessage}
            >
              ➤
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const linkifyText = (text) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, index) =>
    urlRegex.test(part) ? (
      <a
        key={index}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#029bff] underline break-words whitespace-pre-wrap max-w-full inline-block"
      >
        {part}
      </a>
    ) : (
      <span
        key={index}
        className=" break-words whitespace-pre-wrap max-w-full inline-block"
      >
        {part}
      </span>
    )
  );
};
